import { Component, inject, signal } from '@angular/core';
import { Unit } from 'src/app/models/unit/unit.model';
import { ActivatedRoute, Router } from '@angular/router';
import { AgeTrackService } from 'src/app/services/age-track.service';
import { environment } from 'src/environments/environment';
import { UnitService } from 'src/app/services/unit.service';
import { GoalService } from 'src/app/services/goal.service';
import { DialogService } from 'src/app/services/dialog.service';
import { formatError } from 'src/app/utils/errorMessage.formatter';
import { CurriculumService } from 'src/app/services/curriculum.service';
import { Content } from 'src/app/models/content/content.model';
import { LevelService } from 'src/app/services/level.service';
import { ContentService } from 'src/app/services/content.service';

@Component({
  selector: 'app-units-editor',
  templateUrl: './units-editor.component.html',
  styleUrls: ['./units-editor.component.scss'],
  standalone: false,
})
export class UnitsEditorComponent {
  unit: Unit = {
    order: 0,
    title: '',
    unitId: '',
    contents: ['', '', '', ''],
    contentDetails: [
      { contentId: '', url: '', type: '', title: '', releaseDate: '' },
      { contentId: '', url: '', type: '', title: '', releaseDate: '' },
      { contentId: '', url: '', type: '', title: '', releaseDate: '' },
      { contentId: '', url: '', type: '', title: '', releaseDate: '' },
    ],
    status: 'active',
  };
  contentsTitles = ['Lesson 1 Introduce', 'Lesson 2 Practice', 'Lesson 3 Apply', 'Lesson 4 Review'];
  saving: boolean;
  loading: boolean;
  deleting: boolean;
  emptyMandatoryFields = Array<string>();
  availableAges: Array<{ id: string; label: string }> = [];
  availableLevels: Array<{
    id: number;
    uuid: string;
    label: string;
  }> = [];
  availableGoals: Array<{ id: string; label: string }> = [];
  unifiedCurriculumId: string;
  hasDifferentAge = signal(false);

  private contentService: ContentService = inject(ContentService);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private ageService: AgeTrackService,
    private levelService: LevelService,
    private goalService: GoalService,
    private unitService: UnitService,
    private dialogService: DialogService,
    private curriculumService: CurriculumService
  ) {
    this.route.params.subscribe(data => {
      this.loading = true;
      this.loadAvailableGoals(data.unitId);
      this.loadAvailableLevels(data.unitId);
    });

    this.loadUnifiedCurriculumData();
  }

  loadAvailableGoals(unitId: string) {
    this.goalService.list({ limit: 1000, curriculumId: 3 }).subscribe(response => {
      this.availableGoals = response.goals.map(g => {
        return { id: g.goalId, label: g.title };
      });

      if (unitId === 'new' && this.availableGoals.length > 0) {
        this.unit.goalId = this.availableGoals[0].id;
        this.loadScCurriculumData(unitId);
      } else {
        this.load(unitId);
      }
    });
  }

  loadAvailableAges(unitId: string, curriculumId: string) {
    this.ageService.list({ limit: 1000, curriculum: curriculumId }).subscribe(response => {
      this.availableAges = response.ages.map(a => {
        return { id: a.ageId, label: a.title };
      });

      if (unitId === 'new' && this.availableAges.length > 0) {
        this.unit.ageId = this.availableAges[0].id;
      }

      this.loading = false;
    });
  }

  loadAvailableLevels(unitId: string) {
    this.levelService.list().subscribe(response => {
      this.availableLevels = response.levels.map(level => {
        return {
          id: level.id, // the primary key; has a relationship with a Unit's levelId
          uuid: level.levelId, // the UUID
          label: level.title,
        };
      });

      if (unitId === 'new' && this.availableLevels.length > 0) {
        this.unit.levelId = this.availableLevels[0].id;
      }
    });
  }

  loadScCurriculumData(unitId: string) {
    this.curriculumService.get(environment.scCurriculumSlug).subscribe(response => {
      this.unit.curriculumId = response.curriculum.curriculumId;
      this.loadAvailableAges(unitId, this.unit.curriculumId);
    });
  }

  async loadUnifiedCurriculumData() {
    return await this.curriculumService
      .get(environment.unifiedCurriculumSlug)
      .toPromise()
      .then(response => {
        this.unifiedCurriculumId = response?.curriculum?.curriculumId;
      });
  }

  load(unitId: string) {
    this.unitService.get(unitId).subscribe(
      response => {
        this.loading = false;
        this.unit = response.unit;

        if (!this.unit.contentDetails) {
          this.unit.contentDetails = [];
        }

        if (this.unit.contentDetails.length > 4) {
          this.unit.contentDetails = this.unit.contentDetails.slice(0, 4);
        }

        if (this.unit.contentDetails.length < 4) {
          var contentsCount = this.unit.contentDetails.length;
          for (var i = 0; i < 4 - contentsCount; i++) {
            this.unit.contentDetails.push({
              url: '',
              type: '',
              title: '',
              contentId: '',
              releaseDate: '',
            });
          }
        }
        // it loads the ages for SCC curriculum, since we don't have ages for unified curriculum defined in our DB
        this.curriculumService.get(environment.scCurriculumSlug).subscribe(response => {
          const sccCurriculumId = response?.curriculum?.curriculumId;
          this.loadAvailableAges(unitId, sccCurriculumId);
        });

        // it validates the content ages with the unit's age
        this.validateContentAges();
      },
      errors => {
        this.loading = false;
        this.dialogService.showErrorAlert(formatError(errors?.error));
        this.goBack();
      }
    );
  }

  updateSwitchStatus(unitStatus: boolean) {
    this.unit.status = unitStatus === true ? 'active' : 'draft';
  }

  trySave() {
    if (!this.allFieldsAreValid()) {
      return;
    }

    this.saving = true;
    this.unit.curriculumId = this.unifiedCurriculumId; //override the curriculum id to the unified curriculum id
    this.unitService.save(this.unit).subscribe(
      response => {
        this.saving = false;
        this.dialogService.showSuccessAlert('Save Successful.');
        if (response) {
          this.router.navigate(['/units/' + response.unitId]);
        }
      },
      errors => {
        this.saving = false;
        this.dialogService.showErrorAlert(formatError(errors?.error));
      }
    );
  }

  allFieldsAreValid(): boolean {
    this.emptyMandatoryFields = [];
    if (!this.unit.title) {
      this.emptyMandatoryFields.push('title');
    }
    if (!this.unit.goalId) {
      this.emptyMandatoryFields.push('goalId');
    }
    for (var i = 0; i < this.unit.contents.length; i++) {
      !this.unit.contents[i] && this.emptyMandatoryFields.push('content' + i);
    }

    if (this.emptyMandatoryFields.length > 0) {
      setTimeout(() => {
        var errorMessage = document.querySelector('.error-message');
        errorMessage?.scrollIntoView({ behavior: 'smooth' });
      }, 1000);
    }

    return this.emptyMandatoryFields.length <= 0;
  }

  delete() {
    this.deleting = true;
    this.unitService.delete(this.unit.unitId).subscribe(() => {
      this.deleting = false;
      this.dialogService.showSuccessAlert('Delete Successful.');
      this.router.navigate(['/units']);
    });
  }

  confirmDelete() {
    this.dialogService.showConfirmationDialog({
      type: 'danger',
      confirmButtonLabel: 'Delete',
      onConfirmCallback: () => this.delete(),
      dialogIconUrl: '../../../../assets/images/icons/alert-circle.svg',
      confirmButtonIconUrl: '../../../../assets/images/icons/delete.svg',
      title: 'Are you sure you want to remove ' + this.unit.title + ' unit from the Curriculum?',
      message: 'This action cannot be undone.',
    });
  }

  onGoalChange($event: string) {
    this.unit.goalId = $event;
  }

  onAgeChange($event: string) {
    this.unit.ageId = $event;
    this.validateContentAges();
  }

  onContentDetailsChange($event: Content, i: number) {
    if (!$event) {
      this.unit.contents[i] = '';
      this.unit.contentDetails[i].contentId = '';
      return;
    }

    this.unit.contents[i] = $event.contentId;
    this.unit.contentDetails[i].url = $event.url;
    this.unit.contentDetails[i].type = $event.type;
    this.unit.contentDetails[i].title = $event.title;
    this.unit.contentDetails[i].contentId = $event.contentId;
    this.getContentDetails($event.contentId).then(contentDetails => {
      this.handleContentAgeValidation(contentDetails);
    });
  }

  goBack() {
    this.router.navigate(['/units']);
  }

  async getContentDetails(contentId: string) {
    return await this.contentService
      .get(contentId)
      .toPromise()
      .then(response => {
        return response.content;
      });
  }

  async validateContentAges() {
    for (const contentId of this.unit.contents) {
      const contentDetails = await this.getContentDetails(contentId);

      return this.handleContentAgeValidation(contentDetails);
    }
  }

  handleContentAgeValidation(contentDetails: Content) {
    if (!contentDetails.sccAges.includes(this.unit.ageId)) {
      this.hasDifferentAge.set(true);
      return;
    }
    this.hasDifferentAge.set(false);
  }
}
